import {
  ChangeDetectionStrategy,
  Component,
  inject,
  Input,
} from '@angular/core';
import {
  ChartFacade,
  TreatmentPlanFacade,
  TreatmentStepsFacade,
} from '@principle-theorem/ng-clinical-charting/store';
import { StafferSettingsStoreService } from '@principle-theorem/ng-principle-shared';
import { SelectionListStore } from '@principle-theorem/ng-shared';
import {
  AssociatedPlanCreator,
  TreatmentPlan,
} from '@principle-theorem/principle-core';
import {
  StepAddPosition,
  type IChartedTreatmentSelectionSummary,
  type IStepAddEvent,
  type ITreatmentPlan,
  type ITreatmentStep,
} from '@principle-theorem/principle-core/interfaces';
import {
  type DocumentReference,
  filterUndefined,
  snapshot,
  type WithRef,
} from '@principle-theorem/shared';
import { combineLatest, type Observable, ReplaySubject } from 'rxjs';
import { switchMap } from 'rxjs/operators';
import {
  ChartableSurfaceUpdater,
  type IEditChartableData,
} from '../../../chartable-surface-updater';
import { AddTreatmentToStoreStepProvider } from '../../../charted-surface/store-treatment-plan/add-treatment-to-store-step-provider';
import { StepDragDropGroup } from '../../../step-drag-drop-group';
import { TreatmentProviderService } from '../../../treatment-provider.service';

@Component({
  selector: 'pr-treatment-plan-editor',
  templateUrl: './treatment-plan-editor.component.html',
  styleUrls: ['./treatment-plan-editor.component.sass'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false,
})
export class TreatmentPlanEditorComponent {
  private readonly _treatmentProvider: TreatmentProviderService;
  private readonly _selectionList = inject(
    SelectionListStore<IChartedTreatmentSelectionSummary>,
    { optional: true }
  );

  treatmentPlan$: ReplaySubject<WithRef<ITreatmentPlan>> = new ReplaySubject(1);
  treatmentSteps$: Observable<WithRef<ITreatmentStep>[]>;

  @Input() stepDragDrop = new StepDragDropGroup();
  @Input() disabled = false;

  @Input()
  set plan(plan: WithRef<ITreatmentPlan>) {
    if (plan) {
      this.treatmentPlan$.next(plan);
    }
  }

  constructor(
    private _treatmentPlanFacade: TreatmentPlanFacade,
    private _treatmentStepsFacade: TreatmentStepsFacade,
    private _stafferSettings: StafferSettingsStoreService,
    private _chartStore: ChartFacade,
    treatmentProvider: TreatmentProviderService
  ) {
    this._treatmentProvider = treatmentProvider;
    this.treatmentSteps$ = combineLatest([
      this.treatmentPlan$,
      this._stafferSettings.treatmentPlan$,
    ]).pipe(
      switchMap(([plan, settings]) =>
        this._treatmentPlanFacade
          .getTreatmentSteps$(plan.ref)
          .pipe(
            switchMap((steps) =>
              TreatmentPlan.orderSteps$(plan, steps, settings.sortBy)
            )
          )
      ),
      filterUndefined()
    );
  }

  async deleteStep(step: WithRef<ITreatmentStep>): Promise<void> {
    const plan = await snapshot(this.treatmentPlan$);
    this._treatmentPlanFacade.removeStep(plan, step);
  }

  async addStep(event?: IStepAddEvent): Promise<void> {
    const plan = await snapshot(this.treatmentPlan$);
    let stepRef: DocumentReference<ITreatmentStep>;
    if (event?.position === StepAddPosition.Before) {
      stepRef = await this._treatmentPlanFacade.addStepBefore(
        plan,
        event.stepRef
      );
    } else {
      stepRef = await this._treatmentPlanFacade.addStep(plan, event?.stepRef);
    }
    await this._moveSelectedTreatments(plan, stepRef);
  }

  async moveTreatmentsToStep(step: WithRef<ITreatmentStep>): Promise<void> {
    const plan = await snapshot(this.treatmentPlan$);
    await this._moveSelectedTreatments(plan, step.ref);
  }

  updateStep(step: WithRef<ITreatmentStep>): void {
    this._treatmentStepsFacade.updateTreatmentStep(step.ref.id, step);
  }

  async generateChildPlan(): Promise<void> {
    const plan = await snapshot(this.treatmentPlan$);
    const patient = await snapshot(TreatmentPlan.patient$(plan));
    const associatedPlanCreator = new AssociatedPlanCreator(patient);
    await associatedPlanCreator.getPlanFromChildren(plan);
  }

  async updateChartable(
    data: IEditChartableData & { step: WithRef<ITreatmentStep> }
  ): Promise<void> {
    const { step, ...withoutStep } = data;
    const feeSchedule = await snapshot(
      this._chartStore.currentSchedule$
    );
    return ChartableSurfaceUpdater.updateTreatmentSurfaces(withoutStep, [
      new AddTreatmentToStoreStepProvider(
        this._treatmentStepsFacade,
        step,
        feeSchedule
      ),
    ]);
  }

  private async _moveSelectedTreatments(
    plan: WithRef<ITreatmentPlan>,
    targetStepRef: DocumentReference<ITreatmentStep>
  ): Promise<void> {
    if (!this._selectionList) {
      return;
    }
    await this._treatmentProvider.moveSelectedTreatments(
      this._selectionList,
      plan,
      targetStepRef
    );
  }
}
